import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getAllProjectsReview,
  getProjectConsumption,
  projectConsumptionXlsxUrl,
} from '../../api/reportsApi';
import { usePermissions } from '../PermissionsContext';
import './Reports.css';

// RA-S4 — Project Consumption (role-budget) page. Two views on the
// same tree: hierarchy (phase → task → WI) and a flat pivot-ready
// view. Both share the same COLS definitions so a funnel active in
// one view lives in the other. Excel download hits the server-side
// xlsx endpoint (numbers stay numbers).
//
// The mockup lives at docs/presentations/hybrid-pm-concept/role-budget-view.html;
// this page renders 1:1 against it against real server data.

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d} ${MON[Number(m) - 1]} ${y.slice(2)}`;
};
const fmtInt = (v) => Math.round(Number(v) || 0);
const eur = (n) => (n < 0 ? '−' : '') + fmtInt(Math.abs(n)).toLocaleString('en-US') + ' €';
const statusLetter = (s) => (s ? s.charAt(0) : '');

// % delta: (consumed - paid) / paid; null when paid==0 (WIs never carry
// paid — those show the mockup's "непривързан" tag).
const pctDelta = (paid, consumed) => {
  if (!paid) return null;
  return Math.round(((consumed - paid) / paid) * 100);
};
const marginPct = (sold, margin) => (sold > 0 ? Math.round((margin / sold) * 100) : 0);

// Column key → shared header + value extractor. Every column can be
// funneled (Excel rule); the funnel value is the STRINGIFIED display
// value so the checklist can group by the same text the cell shows.
const COLS = [
  { k: 'phase', hdr: 'Phase', val: (n, ctx) => phaseName(n, ctx) },
  { k: 'task', hdr: 'Task', val: (n, ctx) => taskName(n, ctx) },
  { k: 'name', hdr: 'Work Item', val: (n) => n.name },
  { k: 'status', hdr: 'Status', val: (n) => n.status || '—' },
  { k: 'role', hdr: 'Role', val: (n) => n.roleName || '(no role)' },
  { k: 'from', hdr: 'From', val: (n) => fmtDate(n.plannedStart) },
  { k: 'to', hdr: 'To', val: (n) => fmtDate(n.plannedFinish) },
  { k: 'paid', hdr: 'Paid h', val: (n) => String(fmtInt(n.paidHours)) },
  { k: 'cons', hdr: 'Consumed h', val: (n) => String(fmtInt(n.consumedHours)) },
  {
    k: 'pct', hdr: '%',
    val: (n) => {
      const p = pctDelta(n.paidHours, n.consumedHours);
      return p === null ? 'unattributed' : (p > 0 ? '+' : '') + p + '%';
    },
  },
  { k: 'sold', hdr: 'Sold', val: (n) => (n.sold ? eur(n.sold) : '—') },
  { k: 'spent', hdr: 'Spent', val: (n) => (n.spent ? eur(n.spent) : '—') },
  {
    k: 'bal', hdr: 'Balance',
    // QLOG-S2.1 §B — Balance is meaningful only where a budget was
    // sold (task rows); WI leaves carry sold=0 and would otherwise
    // print a misleading −spent.
    val: (n) => (n.sold ? eur((n.sold || 0) - (n.spent || 0)) : '—'),
  },
  { k: 'csold', hdr: 'Cost sold', val: (n) => (n.cSold ? eur(n.cSold) : '—') },
  { k: 'cspent', hdr: 'Cost spent', val: (n) => (n.cSpent ? eur(n.cSpent) : '—') },
  {
    k: 'margeur', hdr: 'Margin €',
    // QLOG-S2.1 §B — same rule as `bal`.
    val: (n) => (n.sold ? eur((n.sold || 0) - (n.cSpent || 0)) : '—'),
  },
  {
    k: 'margpct', hdr: 'Margin %',
    val: (n) => {
      const sold = n.sold || 0;
      if (sold <= 0) return '—';
      return marginPct(sold, sold - (n.cSpent || 0)) + '%';
    },
  },
];
const colByKey = (k) => COLS.find((c) => c.k === k);

// Phase/Task helpers — used by both views and by the funnel-value
// enumerator. Level-2 rows walk up via parentId; other levels return
// their own name / — as the mockup does.
function findAncestor(nodes, node, level) {
  if (!node || node.parentId == null) return null;
  const parent = nodes.find((n) => n.id === node.parentId && n.level === node.level - 1);
  if (!parent) return null;
  if (parent.level === level) return parent;
  return findAncestor(nodes, parent, level);
}
function phaseName(n, ctx) {
  if (n.level === 0) return n.name;
  const p = findAncestor(ctx.nodes, n, 0);
  return p ? p.name : '—';
}
function taskName(n, ctx) {
  if (n.level === 1) return n.name;
  if (n.level === 0) return '—';
  const p = findAncestor(ctx.nodes, n, 1);
  return p ? p.name : '—';
}

const flatClass = {
  sold: 'n sellcol', spent: 'n sellcol', bal: 'n sellcol',
  csold: 'n costcol', cspent: 'n costcol',
  margeur: 'n margcol', margpct: 'n margcol',
  paid: 'n', cons: 'n', pct: 'n',
};

export default function ProjectConsumptionPage() {
  const { hasPermission, loading: permLoading } = usePermissions();
  const allowed = hasPermission?.('Timesheet.ViewProjectReports');

  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [flat, setFlat] = useState(false);
  const [sortOver, setSortOver] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [drawerWiId, setDrawerWiId] = useState(null);
  // Per-column active filters — keyed by COLS.k → array of accepted string values.
  const [filters, setFilters] = useState(() => Object.fromEntries(COLS.map((c) => [c.k, []])));
  const [openFunnel, setOpenFunnel] = useState(null);
  const [funnelQ, setFunnelQ] = useState('');

  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const dto = await getAllProjectsReview('All', 'ProjectToDate');
        if (cancelled) return;
        const list = dto?.projects || [];
        setProjects(list);
        if (list.length > 0) setProjectId(String(list[0].projectId));
      } catch (e) {
        if (cancelled) return;
        const msg = e?.response?.data || e.message;
        setError(typeof msg === 'string' ? msg : 'Failed to load project list.');
      }
    })();
    return () => { cancelled = true; };
  }, [allowed]);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError('');
    try {
      const dto = await getProjectConsumption(Number(projectId));
      setData(dto);
      setCollapsed(new Set());
      setDrawerWiId(null);
      setFilters(Object.fromEntries(COLS.map((c) => [c.k, []])));
    } catch (e) {
      const msg = e?.response?.data || e.message;
      setError(typeof msg === 'string' ? msg : 'Failed to load consumption.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const nodes = useMemo(() => data?.nodes || [], [data]);
  const ctx = useMemo(() => ({ nodes }), [nodes]);
  const wiNodes = useMemo(() => nodes.filter((n) => n.level === 2), [nodes]);
  const hasFilter = () => COLS.some((c) => (filters[c.k] || []).length);

  // Funnel enumerates values from WI leaves only (the mockup rule);
  // summary rows never contribute to the checklist — otherwise the
  // task/phase names would show up as "options" for the Role column.
  const funnelValues = (k) => {
    const col = colByKey(k);
    const set = new Set(wiNodes.map((n) => col.val(n, ctx)));
    return [...set].sort();
  };
  const passFilters = (n) => {
    if (n.level !== 2) return true;
    return COLS.every((c) => (filters[c.k] || []).length === 0 || filters[c.k].includes(c.val(n, ctx)));
  };

  const isCollapsed = (n) => collapsed.has(n.id);
  const hiddenByCollapse = (n) => {
    if (n.level === 0) return false;
    // walk up: any collapsed ancestor hides me.
    let cur = n;
    while (cur.parentId != null) {
      const parent = nodes.find((x) => x.id === cur.parentId && x.level === cur.level - 1);
      if (!parent) break;
      if (collapsed.has(parent.id)) return true;
      cur = parent;
    }
    return false;
  };
  const descendantLeaves = (n) => {
    if (n.level === 2) return [n];
    return nodes
      .filter((x) => x.parentId === n.id && x.level === n.level + 1)
      .flatMap((x) => descendantLeaves(x));
  };

  // Aggregates a summary row from its leaves — the RA-S4 §1.1 rule.
  // Used only for the filtered / sort-by-overconsumption views; the
  // server already ships summary rows with the same math for the
  // unfiltered case.
  const aggregateFromLeaves = (leaves) => {
    const t = { paid: 0, consumed: 0, sold: 0, spent: 0, cSold: 0, cSpent: 0 };
    leaves.forEach((n) => {
      t.paid += n.paidHours;
      t.consumed += n.consumedHours;
      t.sold += n.sold;
      t.spent += n.spent;
      t.cSold += n.cSold;
      t.cSpent += n.cSpent;
    });
    return t;
  };

  const toggleCollapsed = (id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleFunnel = (k, e) => {
    if (e) e.stopPropagation();
    setOpenFunnel((prev) => (prev === k ? null : k));
    setFunnelQ('');
  };
  const toggleFilterValue = (k, v, e) => {
    if (e) e.stopPropagation();
    setFilters((prev) => {
      const cur = prev[k] || [];
      const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
      return { ...prev, [k]: next };
    });
  };
  const clearFilter = (k, e) => {
    if (e) e.stopPropagation();
    setFilters((prev) => ({ ...prev, [k]: [] }));
  };

  // Global "click anywhere else" closes the open funnel — mirrors the mockup.
  useEffect(() => {
    if (!openFunnel) return undefined;
    const h = () => setOpenFunnel(null);
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, [openFunnel]);

  if (permLoading) return <div className="reports-page">Loading permissions…</div>;
  if (!allowed) return <div className="reports-page">You don't have access to this report.</div>;

  const projectMeta = projects.find((p) => String(p.projectId) === projectId);
  const project = data ? { name: data.projectName, status: data.status } : (projectMeta ? { name: projectMeta.projectName, status: projectMeta.status } : null);
  const noRoleCount = data?.billableWiWithoutRole || 0;

  const renderFunnel = (k) => {
    const active = (filters[k] || []).length > 0;
    return (
      <>
        <span
          className={`pc-fun${active ? ' act' : ''}`}
          data-testid={`pc-fun-${k}`}
          onClick={(e) => toggleFunnel(k, e)}
        >▼</span>
        {openFunnel === k && (
          <div className="pc-fpanel" onClick={(e) => e.stopPropagation()} data-testid={`pc-fpanel-${k}`}>
            <input
              type="text"
              value={funnelQ}
              onChange={(e) => setFunnelQ(e.target.value)}
              placeholder="search…"
              data-testid={`pc-fsearch-${k}`}
            />
            <div className="vals">
              {funnelValues(k)
                .filter((v) => !funnelQ || String(v).toLowerCase().includes(funnelQ.toLowerCase()))
                .map((v) => (
                  <label key={v}>
                    <input
                      type="checkbox"
                      checked={(filters[k] || []).includes(v)}
                      onChange={(e) => toggleFilterValue(k, v, e)}
                    /> {v}
                  </label>
                ))}
            </div>
            <span className="clr" onClick={(e) => clearFilter(k, e)}>Clear</span>
          </div>
        )}
      </>
    );
  };

  const pctSpan = (paid, consumed) => {
    const p = pctDelta(paid, consumed);
    if (p === null) return <span className="pc-na">unattributed</span>;
    return <span className={p > 0 ? 'pc-over' : p < 0 ? 'pc-under' : ''}>{(p > 0 ? '+' : '') + p + '%'}</span>;
  };
  const balSpan = (v, hasMoney) => {
    if (!hasMoney) return <span className="pc-na">—</span>;
    return <span className={v >= 0 ? 'pc-under' : 'pc-over'}>{eur(v)}</span>;
  };
  const marginEurSpan = (margin, hasMoney) => {
    if (!hasMoney) return <span className="pc-na">—</span>;
    return <span className={margin >= 0 ? 'pc-under' : 'pc-over'}>{eur(margin)}</span>;
  };
  const marginPctSpan = (sold, margin, hasMoney) => {
    if (!hasMoney || sold <= 0) return <span className="pc-na">—</span>;
    const m = marginPct(sold, margin);
    return <span className={m >= 30 ? 'pc-under' : (m < 15 ? 'pc-over' : '')}>{m + '%'}</span>;
  };

  const renderHierarchyRow = (n) => {
    const cls = n.level === 0 ? 'pc-l0' : (n.level === 1 ? 'pc-l1' : 'pc-wi');
    const hasKids = n.level < 2 && nodes.some((x) => x.parentId === n.id && x.level === n.level + 1);
    const eb = hasKids ? (
      <span
        className="pc-eb"
        data-testid={`pc-eb-${n.level}-${n.id}`}
        onClick={(e) => { e.stopPropagation(); toggleCollapsed(n.id); }}
      >{isCollapsed(n) ? '►' : '▼'}</span>
    ) : <span className="pc-eb" />;
    const nameInner = (
      <>
        {n.level === 2 && n.status && (
          <span className={`pc-mi ${statusLetter(n.status)}`}>{statusLetter(n.status)}</span>
        )}
        {n.name}
        {n.level === 2 && n.roleName && <span className="pc-role">{n.roleName}</span>}
        {n.level === 2 && !n.roleName && <span className="pc-role" style={{ color: '#a86a00' }}>—</span>}
      </>
    );
    // QLOG-S2.1 §B — Balance and Margin are meaningful only where a
    // budget was sold. Sold lives on the TASK row (level 1); WI leaves
    // carry sold=0 by construction. Gating on sold>0 hides the misleading
    // "−spent / −cSpent" that leaves used to paint in red — the actuals
    // still show in the Spent / Cost spent / Consumed columns on the same
    // row. Task rows keep their real numbers.
    const hasSold = (n.sold || 0) > 0;
    const balance = (n.sold || 0) - (n.spent || 0);
    const margin = (n.sold || 0) - (n.cSpent || 0);
    // Row click on level-2 opens the drawer; l0/l1 never open (spec §2.4).
    const rowProps = n.level === 2
      ? { className: `${cls} click`, onClick: () => setDrawerWiId(n.id), 'data-testid': `pc-row-wi-${n.id}` }
      : { className: cls, 'data-testid': `pc-row-l${n.level}-${n.id}` };
    return (
      <tr key={`${n.level}-${n.id}`} {...rowProps}>
        <td className={`pc-name pc-ind${n.level}`}>{eb}{nameInner}</td>
        <td>{n.level === 2 ? (n.status || '') : ''}</td>
        <td>{n.level === 2 ? (n.roleName || '') : ''}</td>
        <td>{fmtDate(n.plannedStart)}</td>
        <td>{fmtDate(n.plannedFinish)}</td>
        <td className="n">{n.paidHours ? fmtInt(n.paidHours) : '—'}</td>
        <td className="n">{n.consumedHours ? fmtInt(n.consumedHours) : '—'}</td>
        <td className="pct">{pctSpan(n.paidHours, n.consumedHours)}</td>
        <td className="money sellcol">{n.sold ? eur(n.sold) : '—'}</td>
        <td className="money sellcol">{n.spent ? eur(n.spent) : '—'}</td>
        <td className="money sellcol">{balSpan(balance, hasSold)}</td>
        <td className="money costcol">{n.cSold ? eur(n.cSold) : '—'}</td>
        <td className="money costcol">{n.cSpent ? eur(n.cSpent) : '—'}</td>
        <td className="money margcol">{marginEurSpan(margin, hasSold)}</td>
        <td className="pct margcol">{marginPctSpan(n.sold, margin, hasSold)}</td>
      </tr>
    );
  };

  // Hierarchy visibility: sort-by-overconsumption flattens to WI leaves;
  // otherwise we walk the tree, hide collapsed subtrees, and filter WIs.
  const hierarchyVisible = () => {
    if (sortOver) {
      return [...wiNodes]
        .filter(passFilters)
        .sort((a, b) => {
          const pa = pctDelta(a.paidHours, a.consumedHours);
          const pb = pctDelta(b.paidHours, b.consumedHours);
          return (pb === null ? -999 : pb) - (pa === null ? -999 : pa);
        });
    }
    return nodes.filter((n) => {
      if (hiddenByCollapse(n)) return false;
      if (n.level === 2) return passFilters(n);
      // Level 0/1: visible if at least one descendant WI passes.
      return descendantLeaves(n).some(passFilters);
    });
  };

  // RA-S4.1 — TOTAL by mode. Paid / Sold / Cost-Sold live on task rows
  // (server aggregates them up to phases); WI leaves carry 0 on those.
  // Unfiltered natural view: sum over level-0 phase rows — the server
  // already carries the correct subtree aggregate on every field, so
  // TOTAL equals the sum of the phase rows the user sees. Filtered or
  // sort-by-overconsumption: consumed / spent / cSpent from the visible
  // WI leaves (actuals live there); paid / sold / cSold from the DISTINCT
  // level-1 ancestor tasks of those leaves (each task once — otherwise
  // two visible siblings under one task would double-count its sold).
  const computeTotal = () => {
    const t = { paid: 0, consumed: 0, sold: 0, spent: 0, cSold: 0, cSpent: 0 };
    if (!hasFilter() && !sortOver) {
      nodes.forEach((n) => {
        if (n.level !== 0) return;
        t.paid += n.paidHours || 0;
        t.consumed += n.consumedHours || 0;
        t.sold += n.sold || 0;
        t.spent += n.spent || 0;
        t.cSold += n.cSold || 0;
        t.cSpent += n.cSpent || 0;
      });
      return t;
    }
    const visibleLeaves = hierarchyVisible().filter((n) => n.level === 2);
    visibleLeaves.forEach((n) => {
      t.consumed += n.consumedHours || 0;
      t.spent += n.spent || 0;
      t.cSpent += n.cSpent || 0;
    });
    const seenTasks = new Set();
    visibleLeaves.forEach((leaf) => {
      const task = findAncestor(nodes, leaf, 1);
      if (task && !seenTasks.has(task.id)) {
        seenTasks.add(task.id);
        t.paid += task.paidHours || 0;
        t.sold += task.sold || 0;
        t.cSold += task.cSold || 0;
      }
    });
    return t;
  };
  const T = computeTotal();
  const hasMoneyT = T.sold > 0 || T.spent > 0;
  const balT = T.sold - T.spent;
  const marT = T.sold - T.cSpent;

  const rowsToRender = hierarchyVisible();

  const groupHeader = () => (
    <tr>
      <th rowSpan={2}>{colByKey('name').hdr}{renderFunnel('name')}</th>
      <th rowSpan={2}>{colByKey('status').hdr}{renderFunnel('status')}</th>
      <th rowSpan={2}>{colByKey('role').hdr}{renderFunnel('role')}</th>
      <th rowSpan={2}>{colByKey('from').hdr}{renderFunnel('from')}</th>
      <th rowSpan={2}>{colByKey('to').hdr}{renderFunnel('to')}</th>
      <th className="grp" colSpan={3}>Hours</th>
      <th className="grp gsell" colSpan={3}>Sell rate</th>
      <th className="grp gcost" colSpan={2}>Internal cost</th>
      <th className="grp gmarg" colSpan={2}>Margin (sold − cost)</th>
    </tr>
  );
  const subHeader = () => (
    <tr>
      <th className="n">Paid h{renderFunnel('paid')}</th>
      <th className="n">Consumed h{renderFunnel('cons')}</th>
      <th className="n">%{renderFunnel('pct')}</th>
      <th className="n gsell">Sold{renderFunnel('sold')}</th>
      <th className="n gsell">Spent{renderFunnel('spent')}</th>
      <th className="n gsell">Balance{renderFunnel('bal')}</th>
      <th className="n gcost">Sold{renderFunnel('csold')}</th>
      <th className="n gcost">Spent{renderFunnel('cspent')}</th>
      <th className="n gmarg">Margin{renderFunnel('margeur')}</th>
      <th className="n gmarg">%{renderFunnel('margpct')}</th>
    </tr>
  );

  const renderHierarchy = () => (
    <div className="pc-wrap">
      <table className="pc-g" data-testid="pc-table-hier">
        <thead>{groupHeader()}{subHeader()}</thead>
        <tbody>
          {rowsToRender.map(renderHierarchyRow)}
          <tr className="pc-total" data-testid="pc-total">
            <td className="pc-name">TOTAL {(hasFilter() || sortOver) ? '(filtered)' : ''}</td>
            <td /><td /><td /><td />
            <td className="n">{fmtInt(T.paid)}</td>
            <td className="n">{fmtInt(T.consumed)}</td>
            <td className="pct">{pctSpan(T.paid, T.consumed)}</td>
            <td className="money sellcol">{eur(T.sold)}</td>
            <td className="money sellcol">{eur(T.spent)}</td>
            <td className="money sellcol">{balSpan(balT, hasMoneyT)}</td>
            <td className="money costcol">{eur(T.cSold)}</td>
            <td className="money costcol">{eur(T.cSpent)}</td>
            <td className="money margcol">{marginEurSpan(marT, hasMoneyT)}</td>
            <td className="pct margcol">{marginPctSpan(T.sold, marT, hasMoneyT)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const renderFlat = () => {
    const wis = wiNodes.filter(passFilters);
    return (
      <>
        <div className="pc-wrap">
          <table className="pc-g" data-testid="pc-table-flat">
            <thead>
              <tr>
                {COLS.map((c) => (
                  <th key={c.k} className={flatClass[c.k] || ''}>{c.hdr}{renderFunnel(c.k)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {wis.map((n) => (
                <tr key={n.id} className="pc-wi" data-testid={`pc-flat-row-${n.id}`}>
                  {COLS.map((c) => (
                    <td key={c.k} className={flatClass[c.k] || ''}>{c.val(n, ctx)}</td>
                  ))}
                </tr>
              ))}
              <tr className="pc-total" data-testid="pc-flat-total">
                {/* First three cols = Phase / Task / Work Item; skip
                    to keep the reference TOTAL aligned. */}
                <td>TOTAL {hasFilter() ? '(filtered)' : ''}</td>
                <td /><td /><td /><td /><td /><td />
                <td className="n">{fmtInt(T.paid)}</td>
                <td className="n">{fmtInt(T.consumed)}</td>
                <td className="pct">{pctSpan(T.paid, T.consumed)}</td>
                <td className="n sellcol">{eur(T.sold)}</td>
                <td className="n sellcol">{eur(T.spent)}</td>
                <td className="n sellcol">{eur(balT)}</td>
                <td className="n costcol">{eur(T.cSold)}</td>
                <td className="n costcol">{eur(T.cSpent)}</td>
                <td className="n margcol">{eur(marT)}</td>
                <td className="n margcol">{T.sold > 0 ? marginPct(T.sold, marT) + '%' : '—'}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="pc-flat-note">
          Flat view for Excel: every work item is its own row and <b>Phase</b> / <b>Task</b> are
          columns. Pivot in Excel by Phase / Task / Role / Status — the totals come from the pivot
          (SUM). The TOTAL row above is for reference only and is NOT included in the download.
        </div>
      </>
    );
  };

  const drawerWi = drawerWiId != null ? nodes.find((n) => n.id === drawerWiId && n.level === 2) : null;

  return (
    <div className="reports-page">
      <h1 className="page-title">Project Consumption</h1>
      <p className="page-sub">
        Sold vs. worked, per role. Money is derived from role sell/cost rates
        (no per-assignment overrides).
      </p>

      <div className="filter-bar">
        <label htmlFor="pc-project">Project</label>
        <select
          id="pc-project"
          data-testid="pc-project"
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
        >
          {projects.length === 0 && <option value="">— no projects —</option>}
          {projects.map((p) => (
            <option key={p.projectId} value={p.projectId}>{p.projectName}</option>
          ))}
        </select>
        {project && <span className="page-sub" data-testid="pc-project-sub">{project.name} · {project.status}</span>}
        {noRoleCount > 0 && (
          <span className="pc-chip-noroles" data-testid="pc-chip-noroles" title="Billable work items without a role — cost = 0 until a PM picks one">
            {noRoleCount} billable WI{noRoleCount === 1 ? '' : 's'} without a role
          </span>
        )}
        <div className="spacer" />
        <a
          className="pc-ctl"
          data-testid="pc-xlsx"
          href={projectId ? projectConsumptionXlsxUrl(Number(projectId)) : '#'}
          download
        >⤓ Download .xlsx</a>
        <span
          className={`pc-ctl${flat ? ' on' : ''}`}
          data-testid="pc-toggle-flat"
          onClick={() => { setFlat((v) => !v); setSortOver(false); }}
        >⤓ Flat / Excel view</span>
        {!flat && (
          <span
            className={`pc-ctl${sortOver ? ' on' : ''}`}
            data-testid="pc-toggle-sort"
            onClick={() => setSortOver((v) => !v)}
          >Sort by overconsumption {sortOver ? '▼' : ''}</span>
        )}
        {!flat && sortOver && (
          <span className="pc-ctl" data-testid="pc-natural-order" onClick={() => setSortOver(false)}>Natural order</span>
        )}
      </div>

      {error && <div className="report-error" data-testid="pc-error">{error}</div>}
      {loading ? (
        <div className="empty-note" data-testid="pc-loading">Loading…</div>
      ) : !data ? (
        <div className="empty-note">Pick a project to view its consumption.</div>
      ) : flat ? renderFlat() : renderHierarchy()}

      {drawerWi && <ConsumptionWiDrawer wi={drawerWi} onClose={() => setDrawerWiId(null)} />}
    </div>
  );
}

// RA-S4 §2.2 — WI drawer. Mirrors the mockup: hours row, sell-money
// balance cards, cost + margin cards, per-consultant table (from
// drawer.who[]), and a Deliverable stub. All data comes from the same
// tree node — no second round-trip.
function ConsumptionWiDrawer({ wi, onClose }) {
  const hasMoney = (wi.sold || 0) > 0 || (wi.spent || 0) > 0;
  const bal = (wi.sold || 0) - (wi.spent || 0);
  const margin = (wi.sold || 0) - (wi.cSpent || 0);
  const pct = pctDelta(wi.paidHours, wi.consumedHours);
  const mpct = wi.sold > 0 ? marginPct(wi.sold, margin) : null;
  return (
    <>
      <div className="pc-ov" onClick={onClose} data-testid="pc-drawer-ov" />
      <div className="pc-dr" data-testid="pc-drawer">
        <div className="hd">
          <h3>
            {wi.status && (
              <span className={`pc-mi ${statusLetter(wi.status)}`}>{statusLetter(wi.status)}</span>
            )}
            {wi.name}
          </h3>
          <span className="x" onClick={onClose} data-testid="pc-drawer-close">✕</span>
        </div>
        <div className="bd">
          <div className="sec">Hours</div>
          <div className="kv">
            <span className="k">Role</span><span>{wi.roleName || '—'}{!wi.roleName && ' (unattributed)'}</span>
            <span className="k">Paid</span><span>{wi.paidHours ? `${fmtInt(wi.paidHours)} h` : '—'}</span>
            <span className="k">Consumed</span><span>{fmtInt(wi.consumedHours)} h</span>
            <span className="k">Delta</span>
            <span className={pct === null ? 'pc-na' : (pct > 0 ? 'pc-over' : 'pc-under')}>
              {pct === null ? 'unattributed' : (pct > 0 ? '+' : '') + pct + '%'}
            </span>
            <span className="k">Period</span><span>{fmtDate(wi.plannedStart)} – {fmtDate(wi.plannedFinish)}</span>
          </div>

          <div className="sec">Money balance (sell rate)</div>
          <div className="bal">
            <div className="c"><div className="l">Sold</div><div className="v">{hasMoney ? eur(wi.sold) : '—'}</div></div>
            <div className="c"><div className="l">Spent</div><div className="v">{hasMoney ? eur(wi.spent) : '—'}</div></div>
            <div className="c">
              <div className="l">Balance</div>
              <div className="v" style={{ color: bal >= 0 ? '#2a6b2a' : '#c0392b' }}>
                {hasMoney ? eur(bal) : '—'}
              </div>
            </div>
          </div>

          <div className="sec">Internal cost</div>
          <div className="bal">
            <div className="c"><div className="l">Cost sold</div><div className="v">{hasMoney ? eur(wi.cSold) : '—'}</div></div>
            <div className="c"><div className="l">Cost spent</div><div className="v">{hasMoney ? eur(wi.cSpent) : '—'}</div></div>
            <div className="c">
              <div className="l">Margin (sold − cost)</div>
              <div className="v" style={{ color: margin >= 0 ? '#2a6b2a' : '#c0392b' }}>
                {hasMoney ? eur(margin) : '—'}
              </div>
            </div>
          </div>
          {mpct !== null && (
            <div className="kv" style={{ marginTop: 6 }}>
              <span className="k">Margin %</span>
              <span className={mpct >= 30 ? 'pc-under' : (mpct < 15 ? 'pc-over' : '')}>{mpct}%</span>
            </div>
          )}

          <div className="sec">Who worked (why the numbers)</div>
          <table className="t" data-testid="pc-drawer-who">
            <thead>
              <tr><th>Consultant</th><th>Role</th><th className="n">Hours</th></tr>
            </thead>
            <tbody>
              {(wi.who || []).length === 0 ? (
                <tr><td colSpan={3} style={{ color: '#999' }}>no hours logged</td></tr>
              ) : wi.who.map((w) => (
                <tr key={w.employeeId} data-testid={`pc-drawer-who-${w.employeeId}`}>
                  <td>{w.employeeName}</td>
                  <td>{w.roleAbbreviation || '—'}</td>
                  <td className="n">{fmtInt(w.hours)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="sec">Deliverable</div>
          <div style={{ fontSize: 12.5, color: '#555', whiteSpace: 'pre-wrap' }}>
            {wi.deliverable || <span style={{ color: '#999', fontStyle: 'italic' }}>— not filled in —</span>}
          </div>
          {wi.acceptanceCriteria && (
            <>
              <div className="sec">Acceptance</div>
              <div style={{ fontSize: 12.5, color: '#555', whiteSpace: 'pre-wrap' }}>{wi.acceptanceCriteria}</div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
